import React, {useEffect} from 'react';

import {withRouter, type WithRouterProps} from 'react-router';
import {PopoverBody, PopoverHeader} from 'reactstrap';
import styled from 'styled-components';

import {UserAvatar} from 'app/components/general/avatars';
import {useCurrentUserState} from 'app/current-user/api';
import {type OverlayProps, PopoverForm} from 'app/general/overlays';
import {useToggleWidgetIsOpen, useWidgetIsOpenState} from 'app/settings/hooks';
import {
  COMMENTS_WIDGET,
  type WidgetIsOpenPayloadType,
} from 'app/settings/slice';

import {CommentFormWithUploader} from './CommentFormWithUploader';

import {useAddComment} from '../api';
import {useTextHighlights} from '../TextHighlightsContextProvider';
import {translateCommentContentType} from '../types';

import type {TextHighlightType} from '../types';
import type {
  AttachedCommentImage,
  CommentableContentType,
  CommentSerializedType,
} from 'sweet-types';

type Props = WithRouterProps & {
  contentType: CommentableContentType;
  objectId: number;
  versionId: number;
  overlayProps: OverlayProps;
  highlight: TextHighlightType | null;
};
export const InlineCommentsPopover = withRouter(
  ({
    objectId,
    contentType,
    versionId,
    overlayProps,
    highlight,
    location,
    router,
  }: Props) => {
    const currentUser = useCurrentUserState().data!;
    const [addComment] = useAddComment();
    const {clearHighlight} = useTextHighlights();

    const widgetProps: WidgetIsOpenPayloadType = {
      contentType: translateCommentContentType(contentType),
      objectId: objectId,
      widgetName: COMMENTS_WIDGET,
    };

    const toggleWidgetIsOpen = useToggleWidgetIsOpen();
    const showingComments = useWidgetIsOpenState(widgetProps);

    useEffect(() => {
      if (!overlayProps.isOpen && highlight != null) {
        clearHighlight(highlight.id);
      }
    }, [overlayProps.isOpen]);

    function handleAddComment(
      comment: string,
      attachments: Array<AttachedCommentImage>
    ) {
      const promise = addComment({
        object_pk: versionId,
        parentId: objectId,
        content_type: contentType,
        comment,
        comment_conversation: {
          highlight_start: highlight!.startParams,
          highlight_end: highlight!.endParams,
          highlight_color: highlight!.color,
          highlight_text: highlight!.selectedText,
          step_id: highlight!.stepId,
        },
        attachments: {images: attachments},
      })
        .unwrap()
        .then((result: CommentSerializedType) => {
          clearHighlight(highlight!.id);
          // append newly added comments id to url for easy highlighting
          const {pathname} = location;
          router.replace({
            pathname,
            hash: `#comment${result?.id}`,
          });
          if (!showingComments) {
            toggleWidgetIsOpen(widgetProps);
          }
        });
      return promise;
    }

    if (highlight == null) {
      return null;
    }

    return (
      <StyledPopoverForm overlayProps={overlayProps}>
        {({close}) => (
          <div className="bg-light py-2 rounded shadow-sm w-100 position-relative">
            <PopoverHeader className="bg-light">
              <div className="w-100">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="d-flex align-items-center pl-1">
                    <UserAvatar user={currentUser} />
                    <span className="ml-1">{currentUser.name}</span>
                  </div>
                  <button
                    className="btn border-0 p-0 pr-3"
                    onClick={(event: React.MouseEvent) => {
                      event.preventDefault();
                      close();
                    }}
                  >
                    <i className="fa fa-times"></i>
                  </button>
                </div>
              </div>
            </PopoverHeader>
            <PopoverBody>
              <CommentFormWithUploader
                className="d-flex justify-content-between align-items-center px-2"
                buttonClass="btn btn-primary"
                onSubmit={handleAddComment}
                contentType={translateCommentContentType(contentType)}
                objectId={objectId}
                showDeletedCheckbox={false}
              />
            </PopoverBody>
          </div>
        )}
      </StyledPopoverForm>
    );
  }
);
const StyledPopoverForm = styled(PopoverForm)`
  .popover {
    max-width: 250px !important;
    width: 90%;
  }
`;
